import * as yup from 'yup';
import { VALIDATION_MESSAGE } from '../constants/Constants';
import { LanguageConstants } from '../constants/LanguageConstants';

const emojiFreeRegex = /^[\w\s.,'!&@()-]+$/;

const profileValidation: any = yup.object().shape({
  profile_account_type: yup
    .string()
    .oneOf(['business', 'individual', 'legacy'])
    .required(),

  name: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .required(VALIDATION_MESSAGE.Field('name'))
        .min(2, VALIDATION_MESSAGE?.MinLength('Name', 2))
        .max(120, VALIDATION_MESSAGE?.MaxLength('Name', 120))
        .matches(/^(?!.*\s{2,}).*$/, 'Name cannot contain consecutive spaces')
        .matches(emojiFreeRegex, LanguageConstants?.Emojis),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  first_name: yup.string().when('profile_account_type', {
    is: (value: string) => value !== 'business',
    then: (s: any) =>
      s
        .required('Please enter first name')
        .min(2, 'First name must be at least 2 characters')
        .max(25, 'First name cannot exceed more than 25 characters')
        .matches(/^(?!.*\s{2,}).*$/, 'First name cannot contain consecutive spaces')
        .matches(emojiFreeRegex, LanguageConstants?.Emojis),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  last_name: yup.string().when('profile_account_type', {
    is: (value: string) => value !== 'business',
    then: (s: any) =>
      s
        .required('Please enter last name')
        .min(2, 'Last name must be at least 2 characters')
        .max(25, 'Last name cannot exceed more than 25 characters')
        .matches(/^(?!.*\s{2,}).*$/, 'Last name cannot contain consecutive spaces')
        .matches(emojiFreeRegex, LanguageConstants?.Emojis),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  company_name: yup.string().nullable().notRequired(),

  business_field_type: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .required(VALIDATION_MESSAGE.Field('business field type'))
        .trim()
        .min(1)
        .max(200, VALIDATION_MESSAGE.MaxLength('Business field type', 200))
        .matches(/^(?!.*\s{2,}).*$/, 'Cannot contain consecutive spaces')
        .matches(emojiFreeRegex, LanguageConstants?.Emojis),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  contact_name: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .required(VALIDATION_MESSAGE.Field('contact name'))
        .trim()
        .min(2, VALIDATION_MESSAGE.MinLength('Contact name', 2))
        .max(120, VALIDATION_MESSAGE.MaxLength('Contact name', 120))
        .matches(/^(?!.*\s{2,}).*$/, 'Cannot contain consecutive spaces')
        .matches(emojiFreeRegex, LanguageConstants?.Emojis),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  company_tax_id: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .required(VALIDATION_MESSAGE.Field('company tax id'))
        .trim()
        .min(2, VALIDATION_MESSAGE.MinLength('Company tax id', 2))
        .max(80, VALIDATION_MESSAGE.MaxLength('Company tax id', 80))
        .matches(/^[A-Za-z0-9\s.\-\/]+$/, 'Please use letters, numbers, and common tax-id characters only'),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  company_address: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .required(VALIDATION_MESSAGE.Field('company address'))
        .trim()
        .min(3, VALIDATION_MESSAGE.MinLength('Company address', 3))
        .max(500, VALIDATION_MESSAGE.MaxLength('Company address', 500)),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  additional_info: yup.string().when('profile_account_type', {
    is: 'business',
    then: (s: any) =>
      s
        .transform((v: unknown) => (v == null ? '' : String(v)))
        .max(500, VALIDATION_MESSAGE.MaxLength('Additional info', 500))
        .nullable(),
    otherwise: (s: any) => s.notRequired().nullable(),
  }),

  email: yup
    .string()
    .required(LanguageConstants?.PLEASE_ENTER_Email)
    .email(LanguageConstants.EMAIL_ADDRESS)
    .test(
      'single-at',
      LanguageConstants.EMAIL_ADDRESS,
      (v) => !v || String(v).split('@').length === 2,
    )
    .matches(VALIDATION_MESSAGE.EMAIL_REGREX, LanguageConstants.EMAIL_ADDRESS)
    .max(150, LanguageConstants?.EMAIL_MAX)
    .trim(),

  phone_number: yup
    .string()
    .nullable()
    .notRequired()
    .test('phone-opt', LanguageConstants.PHONE_NUMBER_VALID, (v: string | null | undefined) => {
      if (!v || String(v).trim() === '') return true;
      return (
        String(v).length >= 7 &&
        String(v).length <= 15 &&
        /^[0-9]*$/.test(v) &&
        !/^0+$/.test(v)
      );
    }),

  phone_country_code: yup.string().nullable().notRequired(),
});

export default profileValidation;
